import React, { useState, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import Image from "../shared/Image";
import Word from "../shared/Word";

import "./Name.scss";
import useConversation from "../shared/useConversation";
import Container from "../shared/Container";
import useVersion from "../shared/useVersion";

import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import Video from "../shared/Video";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import icon from "./name-icon.png";
import {getUserData} from "Services/API";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";
import { logSentryException } from "../../../../../src/services/util";

let ttsStrings;

export default function NameValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.name;

  const { id } = useVersion(props.wordMetadata, {
    1: [
      "PHON",
      "NAME_FLAG",
      "NAME_NATIONALITY",
      "NAME_MEANING",
      "GRAPHEMIC_BREAKDOWN",
    ],
    2: [
      "NAME_PERSON",
      "FUNFACTS_DESCRIPTION",
    ],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: Name1,
    2: Name2,
  }[versionId];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function Name1(props) {
  const { word, wordMetadata, speech } = props;
  const {
    NAME_FLAG,
    NAME_NATIONALITY,
    NAME_MEANING,
    GRAPHEMIC_BREAKDOWN,
    PHON,
  } = wordMetadata;

  const { speak, speakSequence, listenWord, listenPhoneme } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [graphemeIndex, setGraphemeIndex] = useState(0);

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

      // If these don't match, the intervention will crash and it indicates bad data.  Skip intervention and log to Sentry
    if (graphemes.length !== abPhonemes.length) {
      logSentryException(`GRAPHEMIC_BREAKDOWN and PHONEME incorrectly configured for word: ${word}`)
      return props.close();
    }

    try {
      switch (stage) {
        // Speak Intro
        case 0:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.intro_1_part_1,
              {textToSpeak: NAME_NATIONALITY, skipTranslation: true},
              ttsStrings.intro_1_part_2,
              {textToSpeak: NAME_MEANING, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.intro_1_part_1} ${NAME_NATIONALITY} ${ttsStrings.intro_1_part_2} ${NAME_MEANING}`));
          await cancellablePromise(sleep());
          setStage(1);
          break;
        // Sound out instructions
        case 1:
          await cancellablePromise(speak(ttsStrings.ask_sounds));
          await cancellablePromise(sleep());
          setStage(2);
          break;
        // Listen for the phonemes
        case 2:
          for (let i = 0; i < graphemes.length; i++) {
            setGraphemeIndex(i);
            const { status } = await cancellablePromise(listenPhoneme({ file: files[i], targetWord: abPhonemes[i] }));
            if (status === "skip") return props.close();
          }
          setStage(3);
          break;
        // Ask for word, listen for word, and close it out (should break this state up)
        case 3: {
          setGraphemeIndex(-1);
          await cancellablePromise(speak(ttsStrings.ask_word_1));
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle", "silence"],
          }));
          if (status === "skip") return props.close();
          setGraphemeIndex(graphemes.length - 1);

          if (status === "match") {
            if (getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.correct_1,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.correct_1} ${word}.`));
          }
          else{
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.struggle_part_1,
                {textToSpeak: word, skipTranslation: true},
                ttsStrings.struggle_part_2,
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.struggle_part_1} ${word}. ${ttsStrings.struggle_part_2}`));
          }
          props.close();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container onTouchMove={onTouchMove} icon={icon}>
      <div style={{ marginBottom: 30 }}>{renderContent()}</div>
      <ListeningStatus />
    </Container>
  );

  function onTouchMove(e) {
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.pageX, touch.pageY);
    if (el.className.includes("word")) onTouchLetter(el.id);
  }

  function onTouchLetter(i) {
    const index = parseInt(i);
    if (stage !== 3) return;
    if (index === graphemeIndex + 1) setGraphemeIndex(index);
  }

  function renderContent() {
    if (stage === 0)
      return (
        <ReactCountryFlag
          svg
          countryCode={NAME_FLAG}
          style={{ fontSize: 200 }}
        />
      );
    if (stage === 1)
      return (
        <div>
          {graphemes.map((g, i) => (
            <Word key={i} style={{ margin: "0 15px" }}>
              {i === 0 ? g.toUpperCase() : g}
            </Word>
          ))}
        </div>
      );
    if (stage === 2)
      return (
        <div>
          {graphemes.map((g, i) => {
            const isDisabled = i !== graphemeIndex;
            return (
              <Word key={i} style={{ margin: "0 15px" }} disabled={isDisabled}>
                {i === 0 ? g.toUpperCase() : g}
              </Word>
            );
          })}
        </div>
      );
    if (stage === 3)
      return (
        <div>
          {graphemes.map((g, i) => {
            const isDisabled = i > graphemeIndex;
            return (
              <Word
                key={i}
                id={i}
                style={{ margin: "0 15px" }}
                disabled={isDisabled}
                onMouseEnter={() => onTouchLetter(i)}
              >
                {i === 0 ? g.toUpperCase() : g}
              </Word>
            );
          })}
        </div>
      );
  }
}

function Name2(props) {
  const { word, wordMetadata, speech, StudentVideo } = props;
  const {
    NAME_PERSON,
    FUNFACTS_IMG_URL,
    FUNFACTS_DESCRIPTION,
    VIDEO_URL,
    PHONEME_VIDEO_URL,
  } = wordMetadata;

  const { speak, speakSequence, listenWord } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const [stage, setStage] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              {textToSpeak: NAME_PERSON, skipTranslation: true},
              ttsStrings.intro_2,
              {textToSpeak: word, skipTranslation: true},
              ttsStrings.fun_fact,
              {textToSpeak: FUNFACTS_DESCRIPTION, skipTranslation: true},
            ]));
          }
          else {
            await cancellablePromise(speak(`${NAME_PERSON} ${ttsStrings.intro_2} ${word}`));
            await cancellablePromise(speak(`${ttsStrings.fun_fact} ${FUNFACTS_DESCRIPTION}`));
          }

          await cancellablePromise(sleep());
          if (PHONEME_VIDEO_URL || VIDEO_URL) {
            setStage(1);
          } else {
            setStage(2);
          }
          break;
        case 1:
          await cancellablePromise(speak(ttsStrings.lip_sync));
          await cancellablePromise(sleep(500));
          setPlaying(true);
          break;
        case 2:
          await cancellablePromise(speak(ttsStrings.ask_word_2));
          await cancellablePromise(listenWord({ targetWord: word }));
          props.close();
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  function onDonePlayingVideo(){
    if (!props.isMountedRef.current) return;
    setStage(2);
  }

  return <Container icon={icon}>{renderContent()}</Container>;

  function renderContent() {
    if (stage === 0 && FUNFACTS_IMG_URL) return <Image src={FUNFACTS_IMG_URL} />;
    if (stage === 1) {

      if(StudentVideo){
        return (
          <StudentVideo
            onDone={onDonePlayingVideo}
            videoURL={PHONEME_VIDEO_URL ? PHONEME_VIDEO_URL : VIDEO_URL} 
            speech={speech}
            allowReplay={false}
            autoPlay={playing}
          />
        );
      }

      return (
        <Video
          playing={playing}
          src={PHONEME_VIDEO_URL ? PHONEME_VIDEO_URL : VIDEO_URL}
          onEnded={onDonePlayingVideo}
        />
      );
    }
    if (stage === 2)
      return (
        <>
          <div style={{ marginBottom: 30 }}>
            <Word>{word}</Word>
          </div>
          <ListeningStatus />
        </>
      );
  }
}
